import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSound } from '../hooks/useSound';
import { orderService } from '../services';
import type { Order } from '../services/orderService';

const EnquirySuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const orderNumber = searchParams.get('enquiryId') || '';
  const [order, setOrder] = useState<Order | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);
  const { play: playBoomSound } = useSound('/sounds/boom.mp3', { volume: 0.7 });

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
    
    // Fetch order details if order number is provided
    if (orderNumber) {
      orderService.getOrderByNumber(orderNumber).then(response => {
        if (response.success && response.data) {
          setOrder(response.data);
        }
      });
    }
    
    // Play boom sound immediately
    playBoomSound();
    
    // Start animation sequence
    const timeouts = [
      setTimeout(() => setAnimationPhase(1), 500),
      setTimeout(() => setAnimationPhase(2), 1000),
      setTimeout(() => setAnimationPhase(3), 1500),
      setTimeout(() => setShowConfetti(true), 2000),
    ];

    return () => timeouts.forEach(clearTimeout);
  }, [orderNumber, playBoomSound]);

  // Generate confetti particles
  const confettiParticles = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    color: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'][i % 7],
    delay: Math.random() * 3,
    duration: 3 + Math.random() * 2,
    startX: Math.random() * 100,
    rotation: Math.random() * 360,
  }));

  // Generate firework particles
  const fireworkParticles = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    delay: Math.random() * 4,
    x: 20 + Math.random() * 60,
    y: 20 + Math.random() * 60,
  }));

  return (
    <>
      <style>{`
        @keyframes successBounce {
          0% { transform: scale(0) rotate(0deg); opacity: 0; }
          50% { transform: scale(1.2) rotate(180deg); opacity: 1; }
          100% { transform: scale(1) rotate(360deg); opacity: 1; }
        }
        
        @keyframes textReveal {
          0% { transform: translateY(30px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        
        @keyframes pulseGlow {
          0%, 100% { 
            box-shadow: 0 0 20px rgba(34, 197, 94, 0.4), 0 0 40px rgba(34, 197, 94, 0.2), 0 0 60px rgba(34, 197, 94, 0.1);
            transform: scale(1);
          }
          50% { 
            box-shadow: 0 0 30px rgba(34, 197, 94, 0.6), 0 0 60px rgba(34, 197, 94, 0.4), 0 0 90px rgba(34, 197, 94, 0.2);
            transform: scale(1.05);
          }
        }
        
        @keyframes confettiFall {
          0% { 
            transform: translateY(-100vh) translateX(0) rotate(0deg); 
            opacity: 1; 
          }
          100% { 
            transform: translateY(100vh) translateX(var(--random-x, 0)) rotate(720deg); 
            opacity: 0; 
          }
        }
        
        @keyframes fireworkBurst {
          0% { 
            transform: scale(0) translate(0, 0); 
            opacity: 1; 
          }
          100% { 
            transform: scale(1) translate(var(--burst-x, 0), var(--burst-y, 0)); 
            opacity: 0; 
          }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-10px) rotate(5deg); }
          66% { transform: translateY(-5px) rotate(-5deg); }
        }
        
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        
        @keyframes cardEntrance {
          0% { 
            transform: translateY(50px) rotateX(15deg); 
            opacity: 0; 
          }
          100% { 
            transform: translateY(0) rotateX(0deg); 
            opacity: 1; 
          }
        }
        
        @keyframes buttonFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-5px); }
        }
        
        @keyframes checkDraw {
          0% { stroke-dashoffset: 100; }
          100% { stroke-dashoffset: 0; }
        }
        
        .success-icon {
          animation: successBounce 1s ease-out forwards;
        }
        
        .text-reveal {
          animation: textReveal 0.8s ease-out forwards;
        }
        
        .pulse-glow {
          animation: pulseGlow 2s ease-in-out infinite;
        }
        
        .card-entrance {
          animation: cardEntrance 0.8s ease-out forwards;
        }
        
        .button-float {
          animation: buttonFloat 2s ease-in-out infinite;
        }
        
        .shimmer-text {
          background: linear-gradient(90deg, #1f2937 25%, #10b981 50%, #1f2937 75%);
          background-size: 200% 100%;
          background-clip: text;
          -webkit-background-clip: text;
          color: transparent;
          animation: shimmer 2s ease-in-out infinite;
        }
        
        .check-path {
          stroke-dasharray: 100;
          stroke-dashoffset: 100;
          animation: checkDraw 1s ease-out 0.5s forwards;
        }
      `}</style>

      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Floating Emojis */}
          {['🎆', '🧨', '✨', '🎇', '💥', '🎊', '🎉'].map((emoji, i) => (
            <div
              key={i}
              className="absolute text-4xl opacity-20"
              style={{
                left: `${10 + i * 12}%`,
                top: `${15 + (i % 4) * 20}%`,
                animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
                animationDelay: `${i * 0.3}s`,
              }}
            >
              {emoji}
            </div>
          ))}
          
          {/* Confetti Particles */}
          {showConfetti && confettiParticles.map((particle) => (
            <div
              key={particle.id}
              className="absolute w-3 h-3 rounded"
              style={{
                backgroundColor: particle.color,
                left: `${particle.startX}%`,
                animation: `confettiFall ${particle.duration}s linear infinite`,
                animationDelay: `${particle.delay}s`,
                '--random-x': `${(Math.random() - 0.5) * 200}px`,
              } as React.CSSProperties}
            />
          ))}
          
          {/* Firework Burst Effects */}
          {animationPhase >= 2 && fireworkParticles.map((particle) => (
            <div
              key={particle.id}
              className="absolute w-2 h-2 bg-yellow-400 rounded-full"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                animation: `fireworkBurst 1.5s ease-out infinite`,
                animationDelay: `${particle.delay}s`,
                '--burst-x': `${(Math.random() - 0.5) * 100}px`,
                '--burst-y': `${(Math.random() - 0.5) * 100}px`,
              } as React.CSSProperties}
            />
          ))}
        </div>

        <div className="flex items-center justify-center min-h-screen py-12 px-4 relative z-10">
          <div className="max-w-2xl w-full">
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 text-center card-entrance border border-white/20">
              {/* Success Icon with Epic Animation */}
              <div className="relative mb-8">
                <div className="w-32 h-32 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto success-icon pulse-glow relative overflow-hidden">
                  {/* Sparkle overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
                  
                  {/* Check mark with draw animation */}
                  <svg className="w-16 h-16 text-white relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path 
                      className="check-path" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={3} 
                      d="M5 13l4 4L19 7" 
                    />
                  </svg>
                  
                  {/* Ring animations */}
                  <div className="absolute inset-0 rounded-full border-4 border-green-300 animate-ping opacity-20"></div>
                  <div className="absolute inset-0 scale-110 rounded-full border-2 border-green-200 animate-ping opacity-10" style={{ animationDelay: '0.5s' }}></div>
                  <div className="absolute inset-0 scale-125 rounded-full border-2 border-green-100 animate-ping opacity-5" style={{ animationDelay: '1s' }}></div>
                </div>
              </div>

              {/* Success Message with Shimmer Effect */}
              <div className={`mb-6 ${animationPhase >= 1 ? 'text-reveal' : 'opacity-0'}`}>
                <h1 className="text-2xl md:text-3xl font-bold mb-4 shimmer-text">
                  🎉 Enquiry Submitted Successfully! 🎉
                </h1>
                <p className="text-xl text-neutral-600 leading-relaxed">
                  Thank you for your interest! Your fireworks enquiry has been received and our team will contact you soon with the perfect celebration package.
                </p>
              </div>

              {/* Enquiry Details with Staggered Animation */}
              <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-8 border border-blue-100 ${animationPhase >= 2 ? 'card-entrance' : 'opacity-0'}`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-left">
                  <div className="transform hover:scale-105 transition-transform duration-300">
                    <p className="text-sm text-blue-600 mb-2 font-medium">Enquiry Number</p>
                    <p className="font-bold text-2xl text-gray-800 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      {order?.orderNumber || orderNumber || 'Processing...'}
                    </p>
                  </div>
                  <div className="transform hover:scale-105 transition-transform duration-300">
                    <p className="text-sm text-blue-600 mb-2 font-medium">Date</p>
                    <p className="font-bold text-2xl text-gray-800">
                      {order ? new Date(order.createdAt).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      }) : new Date().toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  {order && (
                    <>
                      <div className="transform hover:scale-105 transition-transform duration-300">
                        <p className="text-sm text-blue-600 mb-2 font-medium">Total Amount</p>
                        <p className="font-bold text-2xl text-gray-800">₹{order.total.toLocaleString()}</p>
                      </div>
                      <div className="transform hover:scale-105 transition-transform duration-300">
                        <p className="text-sm text-blue-600 mb-2 font-medium">Items</p>
                        <p className="font-bold text-2xl text-gray-800">{order.items.length} Products</p>
                      </div>
                    </>
                  )}
                  
                </div>
              </div>

              {/* Enquiry Items Summary */}
              {order && order.items.length > 0 && (
                <div className={`mb-8 bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-100 ${animationPhase >= 3 ? 'card-entrance' : 'opacity-0'}`}>
                  <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
                      </svg>
                    </div>
                    Enquiry Summary
                  </h2>
                  <div className="space-y-3">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-white/50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-800">{item.productName}</p>
                          <p className="text-sm text-gray-600">Qty: {item.quantity} × ₹{item.price.toLocaleString()}</p>
                        </div>
                        <p className="font-bold text-gray-800">₹{item.total.toLocaleString()}</p>
                      </div>
                    ))}
                    <div className="border-t pt-3 mt-3 space-y-2">
                      {/* Subtotal */}
                      <div className="flex justify-between items-center text-sm">
                        <p className="text-gray-600">Subtotal</p>
                        <p className="text-gray-800">₹{(order.subtotal || order.total).toLocaleString()}</p>
                      </div>
                      {/* Discount if applied */}
                      {order.discount > 0 && (
                        <div className="flex justify-between items-center text-sm">
                          <p className="text-green-600">
                            Discount {order.couponCode && `(${order.couponCode})`}
                          </p>
                          <p className="text-green-600">-₹{order.discount.toLocaleString()}</p>
                        </div>
                      )}
                      {/* Packaging Charge */}
                      {order.packing > 0 && (
                        <div className="flex justify-between items-center text-sm">
                          <p className="text-gray-600">Packaging Charge</p>
                          <p className="text-gray-800">₹{order.packing.toLocaleString()}</p>
                        </div>
                      )}
                      {/* Total */}
                      <div className="flex justify-between items-center pt-2 border-t">
                        <p className="text-lg font-semibold text-gray-800">Total Amount</p>
                        <p className="text-xl font-bold text-gray-800">₹{order.total.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* What's Next with Enhanced Styling */}
              <div className={`text-left mb-8 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100 ${animationPhase >= 3 ? 'card-entrance' : 'opacity-0'}`}>
                <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                    </svg>
                  </div>
                  What happens next?
                </h2>
                <ul className="space-y-4 text-gray-700">
                  {[
                    "Our team will review your enquiry and prepare a detailed quote",
                    "You'll receive a call from our sales representative within 24 hours",
                    "Payment instructions will be provided once your enquiry is confirmed",
                    "Track your enquiry status in the \"My Enquiries\" section"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-4 transform hover:translate-x-2 transition-transform duration-300">
                      <div className="w-6 h-6 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-white text-sm font-bold">{index + 1}</span>
                      </div>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Action Buttons with Float Animation */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Link
                  to="/enquiries"
                  className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-green-600 hover:to-emerald-700 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-green-500/25 button-float transform hover:rotate-1"
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    View My Enquiries
                  </span>
                </Link>
                <Link
                  to="/"
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/25 transform hover:-rotate-1"
                  style={{ animationDelay: '0.2s' }}
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                    Continue Shopping
                  </span>
                </Link>
              </div>

              {/* Contact Info with Enhanced Design */}
              <div className="pt-8 border-t border-gray-200">
                <p className="text-gray-600 mb-6 text-lg">Need immediate assistance? We're here to help! 🚀</p>
                <div className="flex flex-col sm:flex-row gap-6 justify-center">
                  <a 
                    href="tel:+919876543210" 
                    className="flex items-center justify-center gap-3 text-blue-600 hover:text-blue-700 font-semibold text-lg bg-blue-50 hover:bg-blue-100 px-6 py-3 rounded-xl transition-all duration-300 hover:scale-105 transform hover:rotate-1"
                  >
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
                      </svg>
                    </div>
                    +91 80727 89313
                  </a>
                  <a 
                    href="mailto:democrackers@gmail.com" 
                    className="flex items-center justify-center gap-3 text-purple-600 hover:text-purple-700 font-semibold text-lg bg-purple-50 hover:bg-purple-100 px-6 py-3 rounded-xl transition-all duration-300 hover:scale-105 transform hover:-rotate-1"
                  >
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                      </svg>
                    </div>
                    democrackers@gmail.com
                  </a>
                </div>
              </div>

              {/* Celebration Message */}
              <div className="mt-8 p-6 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl border border-yellow-200">
                <p className="text-2xl mb-2">🎆 Get ready for an amazing celebration! 🎆</p>
                <p className="text-gray-600 leading-relaxed">
                  Your fireworks will bring joy, excitement, and unforgettable memories to your special occasions. 
                  We can't wait to light up your celebrations!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EnquirySuccess;